/**
 * Retell Service
 * Owns the Retell.ai browser client (SDK v3) and web-call creation.
 *
 * v3 vs v2: the browser no longer calls `POST /v2/create-web-call` itself. The SDK
 * creates the call through `/v3/create-web-call` using a *public* key that Retell
 * validates against an allowlist of domains, so no API key ships in the bundle.
 *
 * Ending a call is likewise a local transport disconnect (`session.end()`) — there
 * is no authenticated end-call request to make, and `RetellClient.stopCall()` is
 * deliberately not used here since it requires an API key.
 */

import { RetellClient } from 'retell-client-js-sdk';
import type { SessionHooks, WebCallSession } from 'retell-client-js-sdk';

/**
 * Today's date in French, injected as a dynamic variable so the agent can resolve
 * relative periods ("le mois prochain", "cet été"). Replaces the old static
 * calendar KB.
 *
 * @returns Date formatted as e.g. "lundi 12 octobre 2026"
 */
function currentDateFr(): string {
  return new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export class RetellService {
  private client: RetellClient;

  constructor() {
    const key = import.meta.env.VITE_RETELL_PUBLIC_KEY || '';
    if (!key) {
      console.error(
        '[Retell] VITE_RETELL_PUBLIC_KEY is missing — web calls will be rejected. ' +
          'Create a public key at https://dashboard.retellai.com (accounts/public-keys) ' +
          'and allowlist this domain.'
      );
    }
    this.client = new RetellClient({ key });
  }

  /**
   * Creates and connects a web call.
   *
   * Returns synchronously: the session connects in the background. Await
   * `session.ready` to know the call is live, and read `session.callId` once
   * Retell has created the call (see `waitForCallId` in VoiceInterface — the id
   * lands before the transport finishes connecting).
   *
   * @param agentId Retell agent id
   * @param hooks   Optional lifecycle hooks (onStatus / onEnd / onError / ...)
   * @returns The live call session
   */
  createWebCall(agentId: string, hooks?: SessionHooks): WebCallSession {
    return this.client.createWebCall({
      agent_id: agentId,
      retell_llm_dynamic_variables: {
        current_date: currentDateFr(),
      },
      hooks,
    });
  }
}

// Singleton instance
export const retellService = new RetellService();
